import React from 'react';
import ColorFacetSwatch from './ColorFacetSwatch';
import { useFormat } from '@loomstateorg/shared-frontend/hooks/useFormat';
import { ColorSwatchLinkProps, ProductColorOption, ProductColorSwatchesProps } from './types';

const DEFAULT_MAX_VISIBLE = 5;

const ROW_HEIGHT_CLASS = 'h-20';

const ProductColorSwatches = ({
  colors,
  selectedColorKey,
  onSelect,
  moreHref,
  linkComponent,
  maxVisible = DEFAULT_MAX_VISIBLE,
  reserveSpaceWhenEmpty = true,
  className = '',
  swatchClassName = '',
}: ProductColorSwatchesProps): JSX.Element | null => {
  const { formatMessage } = useFormat({ name: 'product' });

  const visibleColors = colors.slice(0, Math.max(maxVisible, 1));
  const hiddenCount = colors.length - visibleColors.length;

  const handleSelect = (event: React.MouseEvent<HTMLButtonElement>, color: ProductColorOption) => {
    // The whole card links to the product detail page, a swatch only previews a color.
    event.preventDefault();
    event.stopPropagation();
    onSelect?.(color);
    // Mouse clicks focus the button and would keep the color label visible via :focus-within.
    event.currentTarget.blur();
  };

  if (!colors.length) {
    return reserveSpaceWhenEmpty ? (
      <div aria-hidden className={className}>
        <div className={ROW_HEIGHT_CLASS} />
      </div>
    ) : null;
  }

  const renderSwatchSurface = (color: ProductColorOption, isSelected: boolean) => (
    <ColorFacetSwatch
      colorKey={color.key}
      hexColor={color.hexColor}
      isSelected={isSelected}
      sizeClassName="size-16"
      className={swatchClassName}
    />
  );

  const renderColorName = (color: ProductColorOption) => (
    <span
      aria-hidden
      className="pointer-events-none invisible absolute bottom-full left-1/2 z-20 mb-4 -translate-x-1/2 whitespace-nowrap rounded bg-black px-6 py-2 text-11 text-white group-hover/swatch:visible group-focus-within/swatch:visible"
    >
      {color.label}
    </span>
  );

  const isInteractive = colors.length > 1;
  const MoreLink = (linkComponent ?? 'a') as React.ComponentType<ColorSwatchLinkProps>;

  return (
    <div data-component-group="product-color-swatches" data-testid="product-color-swatches" className={className}>
      <div className={`flex ${ROW_HEIGHT_CLASS} items-center gap-8`}>
        {isInteractive ? (
          <div
            role="group"
            aria-label={formatMessage({ id: 'product.colors.available', defaultMessage: 'Available colors' })}
            className="flex items-center gap-8"
          >
            {visibleColors.map((color) => {
              const isSelected = color.key === selectedColorKey;

              return (
                <span key={color.key} className="group/swatch relative flex items-center">
                  <button
                    type="button"
                    data-color-swatch
                    data-testid={`product-color-swatch-${color.key}`}
                    aria-label={color.label}
                    aria-pressed={isSelected}
                    className="flex items-center rounded-full focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-black"
                    onClick={(event) => handleSelect(event, color)}
                  >
                    {renderSwatchSurface(color, isSelected)}
                  </button>
                  {renderColorName(color)}
                </span>
              );
            })}
          </div>
        ) : (
          <span className="group/swatch relative flex items-center" data-testid={`product-color-swatch-${colors[0].key}`}>
            <span role="img" aria-label={colors[0].label}>
              {renderSwatchSurface(colors[0], false)}
            </span>
            {renderColorName(colors[0])}
          </span>
        )}

        {hiddenCount > 0 && moreHref && (
          <MoreLink
            href={moreHref}
            data-testid="product-color-swatches-view-all"
            className="text-11 leading-none underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-black"
            aria-label={formatMessage({
              id: 'product.colors.view.all',
              defaultMessage: 'View all {count} colors on the product page',
              values: { count: colors.length },
            })}
          >
            {`+${hiddenCount}`}
          </MoreLink>
        )}
      </div>
    </div>
  );
};

export default ProductColorSwatches;
